import React, { useState } from "react";
import logo from "../../assets/img/logo.png";
import Basket from "../basket/Basket";
import SearchBar from "../searchBar/SearchBar";
import { Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { Link, useNavigate } from "react-router-dom";
import UserProfile from "../userProfile/UserProfile";
import Declaration from "../declaration/Declaration.js";
import { resourcesLinks } from "../../constants/resourcesLinks";
import { useDispatch, useSelector } from 'react-redux';
import { useCategoriesInfiniteQuery } from "../../hooks/queries/useCategoriesInfiniteQuery";
import { sidebarAction } from "../../store/reducers/sidebarSlice";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
function Navigation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);

  // Fetch lazily on first open, then page through — the menu used to render
  // only whatever the first response carried. Scoped to the buyer's assigned
  // categories server-side; the rest were never browsable.
  const [categoriesOpened, setCategoriesOpened] = useState(false);
  const {
    categories,
    isLoading: loading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useCategoriesInfiniteQuery({ assigned: true, enabled: categoriesOpened });

  return (
    <div className="sticky bg-brand-600 top-0 z-20 drop-shadow-lg">
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-10">
        {user ? (
          <div className="h-16 top-bar lg:h-auto flex items-center justify-between py-4 mx-auto">
            {/* Hamburger Menu - Only in Standalone Mode */}
            <button 
              className="hidden [@media(display-mode:standalone)]:flex lg:[@media(display-mode:standalone)]:hidden items-center justify-center flex-shrink-0 h-auto relative focus:outline-none"
              onClick={() => dispatch(sidebarAction(true))}
            >
              <span className="text-xl text-white">
                <svg
                  stroke="currentColor"
                  fill="none"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-6 h-6 drop-shadow-xl"
                  height="1em"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line x1="17" y1="10" x2="3" y2="10"></line>
                  <line x1="21" y1="6" x2="3" y2="6"></line>
                  <line x1="21" y1="14" x2="3" y2="14"></line>
                  <line x1="17" y1="18" x2="3" y2="18"></line>
                </svg>
              </span>
            </button>

            {/* Logo - Hidden in Standalone Mode */}
            <Link
              to="/home"
              className="!no-underline text-white font-bold mr-3 xl:mr-12 [@media(display-mode:standalone)]:hidden lg:[@media(display-mode:standalone)]:block"
            >
              <span
                style={{
                  boxSizing: "border-box",
                  display: "inline-block",
                  overflow: "hidden",
                  width: "initial",
                  height: "initial",
                  background: "none",
                  opacity: "1",
                  border: "0px",
                  margin: "0px",
                  padding: " 0px",
                  position: "relative",
                  maxWidth: "100%",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    boxSizing: "border-box",
                    display: "block",
                    width: "initial",
                    height: "initial",
                    background: "none",
                    opacity: "1",
                    border: "0px",
                    margin: "0px",
                    padding: " 0px",
                    maxWidth: "100%",
                    alignItems: "center",
                  }}
                >
                  <img className="flex items-center w-48" src={logo} alt="Wholesale Dai" />
                </span>
              </span>
            </Link>

            {/* Search Bar Container - Full Width in Standalone Mode */}
            <div className="lg:block xl:block [@media(display-mode:standalone)]:flex-1 [@media(display-mode:standalone)]:px-4 lg:[@media(display-mode:standalone)]:px-0 flex-grow">
              <div className="items-center justify-between flex mx-auto max-w-screen-4xl px-3 md:px-10 h-12">
                <div className="flex-grow w-full max-w-[70%]">
                  <SearchBar />
                </div>
                <div className="hidden lg:inline-flex items-center gap-6 flex-shrink-0 ml-4">
                  <Popover className="relative">
                    <div className="max-w-7xl mx-auto">
                      <div className="flex justify-between items-center md:justify-start md:space-x-10">
                        <Popover.Group
                          as="nav"
                          className=" md:flex space-x-10 items-center"
                        >
                          {/* <Link
                            className="text-sm font text-white !no-underline hover:!text-brand-600"
                            to="/"
                          >
                            Home
                          </Link> */}
                          <Popover className="relative">
                            {({ close }) => (
                              <>
                                <Popover.Button
                                  onClick={() => setCategoriesOpened(true)}
                                  className={classNames(
                                    // open ? "text-gray-900" : "text-gray-500",
                                    "group inline-flex items-center py-2 text-sm focus:outline-none text-white"
                                  )}
                                >
                                  <span className="text-sm font-bold">Categories</span>
                                  <ChevronDownIcon
                                    className={classNames(
                                      "ml-1 h-3 w-3 group-hover:text-brand-600"
                                    )}
                                    aria-hidden="true"
                                  />
                                </Popover.Button>

                                <Transition
                                  as={Fragment}
                                  enter="transition ease-out duration-200"
                                  enterFrom="opacity-0 translate-y-1"
                                  enterTo="opacity-100 translate-y-0"
                                  leave="transition ease-in duration-150"
                                  leaveFrom="opacity-100 translate-y-0"
                                  leaveTo="opacity-0 translate-y-1"
                                >
                                  <Popover.Panel className="absolute z-10 -ml-1 mt-1 transform w-64 max-w-xs bg-white">
                                    {/* Height cap lives here: without it the list
                                        just grows past the viewport. */}
                                    <div className="rounded-md shadow-lg ring-1 ring-black ring-opacity-5 max-h-[65vh] overflow-y-auto w-full">
                                      <div className="flex flex-col w-full bg-white cursor-pointer">
                                        <div className="w-full">
                                          <div className="relative grid gap-2 p-6">

                                            {loading ? (
                                              // Loading state
                                              <div className="text-center py-4">
                                                <svg
                                                  className="animate-spin h-5 w-5 text-brand-600 mx-auto"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  fill="none"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                  ></circle>
                                                  <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                  ></path>
                                                </svg>
                                                <p className="text-sm text-gray-500 mt-2">Loading categories...</p>
                                              </div>
                                            ) : error ? (
                                              // Error state
                                              <div className="text-center py-4 text-red-500 text-sm">
                                                Failed to load categories
                                              </div>
                                            ) : categories.length > 0 ? (
                                              // Success state
                                              categories.map((category, index) => {
                                                return (
                                                  <span
                                                    key={index}
                                                    className="p-2 flex items-center rounded-md w-full hover:bg-gray-50 hover:text-brand-600 cursor-pointer"
                                                    onClick={() => {
                                                      close();
                                                      navigate(`/search?Category=${category.slug}`);
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        boxSizing: "border-box",
                                                        display: "inline-block",
                                                        overflow: "hidden",
                                                        width: "initial",
                                                        height: "initial",
                                                        background: "none",
                                                        opacity: "1",
                                                        border: "0px",
                                                        margin: "0px",
                                                        padding: "0px",
                                                        position: "relative",
                                                        maxWidth: "100%",
                                                      }}
                                                    >
                                                      <img
                                                        alt={category.name}
                                                        src={category.banner}
                                                        style={{
                                                          inset: "0px",
                                                          boxSizing: "border-box",
                                                          padding: "0px",
                                                          border: "none",
                                                          margin: "auto",
                                                          display: "block",
                                                          width: "16px",
                                                          height: "16px",
                                                          minWidth: "100%",
                                                          maxWidth: "100%",
                                                          minHeight: "100%",
                                                          maxHeight: "100%",
                                                        }}
                                                      />
                                                    </span>
                                                    <div className="ml-3 flex items-center justify-between w-full">
                                                      {category.name}
                                                    </div>
                                                  </span>
                                                );
                                              })
                                            )
                                              : (
                                                // Empty state
                                                <div className="text-center py-4 text-gray-500 text-sm">
                                                  No categories found
                                                </div>
                                              )}
                                            {hasNextPage && (
                                              <button
                                                type="button"
                                                onClick={() => fetchNextPage()}
                                                disabled={isFetchingNextPage}
                                                className="w-full px-3 py-2 text-sm text-brand-600 rounded-md border border-dashed border-brand-300 hover:bg-brand-50 disabled:opacity-60"
                                              >
                                                {isFetchingNextPage ? "Loading…" : "Show more categories"}
                                              </button>
                                            )}

                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </Popover.Panel>
                                </Transition>
                              </>
                            )}
                          </Popover>
                          <Popover className="relative">
                            {({ open }) => (
                              <>
                                <Popover.Button
                                  className={classNames(
                                    // open ? "text-gray-900" : "text-gray-500",
                                    "group rounded-md inline-flex items-center text-sm py-2 text-white hover:text-brand-600 "
                                  )}
                                >
                                  <span className="text-sm font-bold ">About</span>
                                  <ChevronDownIcon
                                    className={classNames(
                                      "ml-1 h-3 w-3 group-hover:text-brand-600 "
                                    )}
                                    aria-hidden="true"
                                  />
                                </Popover.Button>

                                <Transition
                                  as={Fragment}
                                  enter="transition ease-out duration-200"
                                  enterFrom="opacity-0 translate-y-1"
                                  enterTo="opacity-100 translate-y-0"
                                  leave="transition ease-in duration-150"
                                  leaveFrom="opacity-100 translate-y-0"
                                  leaveTo="opacity-0 translate-y-1"
                                >
                                  <Popover.Panel className="absolute z-10 -ml-1 mt-1 transform w-screen max-w-xs bg-white">
                                    <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-y-scroll flex-grow scrollbar w-full h-full">
                                      <div className="relative grid gap-2  px-6 py-6 ">
                                        {resourcesLinks.map((item, index) => (
                                          <span
                                            key={index}
                                            className="p-2 flex  items-center rounded-md hover:!bg-gray-50 w-full hover:!text-brand-600"
                                          >
                                            <item.icon
                                              className="flex-shrink-0 h-4 w-4 "
                                              aria-hidden="true"
                                            />

                                            <Link
                                              to={item.href}
                                              className="!text-black inline-flex items-center justify-between ml-2 text-sm font-medium w-full !no-underline hover:!text-brand-600"
                                            >
                                              {item.name}
                                            </Link>
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  </Popover.Panel>
                                </Transition>
                              </>
                            )}
                          </Popover>
                        </Popover.Group>
                      </div>
                    </div>
                  </Popover>
                </div>

              </div >
            </div >

            <div className="hidden md:hidden md:items-center lg:flex xl:flex absolute inset-y-0 right-0 pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
              <Declaration />
              <Basket className="relative px-5 text-white text-2xl font-bold" />
              <div className="pl-5">
                <UserProfile />
              </div>
            </div>

          </div>
        ) : (
          <div className="h-16 top-bar lg:h-auto flex items-center justify-between py-4 mx-auto">
            {/* Logo */}
            <Link
              to="/"
              className="!no-underline text-white font-bold"
            >
              <span
                style={{
                  boxSizing: "border-box",
                  display: "inline-block",
                  overflow: "hidden",
                  width: "initial",
                  height: "initial",
                  background: "none",
                  opacity: "1",
                  border: "0px",
                  margin: "0px",
                  padding: " 0px",
                  position: "relative",
                  maxWidth: "100%",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    boxSizing: "border-box",
                    display: "block",
                    width: "initial",
                    height: "initial",
                    background: "none",
                    opacity: "1",
                    border: "0px",
                    margin: "0px",
                    padding: " 0px",
                    maxWidth: "100%",
                    alignItems: "center",
                  }}
                >
                  <img className="flex items-center w-48" src={logo} alt="Wholesale Dai" />
                </span>
              </span>
            </Link>

            {/* Auth Buttons */}
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="!no-underline text-white hover:text-brand-200 transition-colors duration-200 px-4 py-2 text-sm font-medium"
              >
                Log In
              </Link>
              <Link
                to="/register"
                className="!no-underline bg-white text-brand-600 hover:bg-brand-50 transition-colors duration-200 px-4 py-2 rounded-lg text-sm font-medium"
              >
                Sign Up
              </Link>
            </div>
          </div>
        )}
      </div>

    </div >
  );
}

export default Navigation;
